import { EventEmitter2 } from 'eventemitter2';
import gsap from 'gsap';
import { Color, Vector3 } from 'three';
import { App } from './App';
import { defaultParams } from './params';
import { EVENTS, STORE_KEYS } from './utils/constants';

class CyberKongzGlViewer extends EventEmitter2 {
  constructor($parent, params = {}) {
    super();
    this._params = {
      ...defaultParams,
      ...params,
      kong: { ...defaultParams.kong, ...params?.kong },
    };
    this._assets = [];
    this.$parent = $parent;
    this._init();
  }

  _init = () => {
    this.app = new App(this.$parent, this._params);
    this.app.init();

    this.setControlsDisabled(this._params.controlsDisabled);
    this.setParallaxDisabled(this._params.parallaxDisabled);
    this.setZoomDisabled(this._params.zoomDisabled);
    this.setPanDisabled(this._params.panDisabled);
    this.setCameraZoom(this._params.cameraZoom);
    this.setCameraPosition(this._params.cameraPosition);
    this.setKongRotation(this._params.kong.rotation);

    this._assets.push({ key: 'kong', path: this._params.kong.body });

    if (this._params.kong.equipments?.length)
      this._assets.push(
        ...this._params.kong.equipments.map((path, i) => {
          return { key: `equip_${i}`, path };
        })
      );

    // if (this._params.kong.weapon) this._assets.push({ key: 'weapon', path: this._params.kong.weapon });
    // if (this._params.kong.shield) this._assets.push({ key: 'shield', path: this._params.kong.shield });
    // if (this._params.kong.armor) this._assets.push({ key: 'armor', path: this._params.kong.armor });
    // if (this._params.kong.hat) this._assets.push({ key: 'hat', path: this._params.kong.hat });
    if (this._params.text?.active && this._params.text.font)
      this._params.text.fontName = this._params.text.font.split('/').pop().split('.')[0];

    if (this._params.arena) this._assets.push({ key: 'arena', path: this._params.arena });
    this.app.load(this._assets);

    this.emit('viewerLoadingStart');
    this.app.state.on(EVENTS.ATTACHED, this._onLoad);
    // this.emit('viewerLoadingProgress');
  };

  _onLoad = () => {
    if (this._params?.kong?.position?.length) {
      const [x, y] = this._params.kong.position;
      this.app.webgl.scene.position.x = x;
      this.app.webgl.scene.position.z = y;
    }
    this.setKongConfig(this._params.kong.config);

    this.emit('viewerLoadingEnd');
    if (this._params.animation) this.setAnimation(this._params.animation);
  };

  get kong() {
    return this._params.kong;
  }

  setKongBody = async (url) =>
    new Promise((resolve) => {
      this._kongTl?.kill();
      this._kongTl = gsap.timeline();

      this._kongTl.add(this.app.webgl.kong.close());
      this._kongTl.add(() => {
        this.emit('kongChangeStart');
      });

      this._kongTl.add(async () => {
        const model = await this.app.core.loader.loadModel('kong', url, true);
        this.emit('kongLoaded');

        this.app.webgl.kong.updateModel(model);
        if (this._params.kong.equipments?.length) this.app.webgl.kong.revertParts();

        this._kongTl.add(this.app.webgl.kong.open());
        this._kongTl.add(() => {
          this.emit('kongChangeEnd');
          resolve();
        });
      });
    });

  setKongConfig = async (config = null) => {
    this.emit('kongConfigChangeStart');
    this.app.webgl.kong.applyConfig(config);
    this.emit('kongConfigChangeEnd');
  };

  async setEquipments(urls) {
    if (typeof urls === 'string') this.setEquipment(urls);
    else {
      await this.preloadEquipments(urls.filter((url) => !url.includes('remove')));
      urls.forEach((url) => {
        this.setEquipment(url);
      });
    }
  }

  preloadEquipments = (urls) => {
    return Promise.all(urls.map((url) => this.app.core.loader.loadModel(`equip`, url, true)));
  };

  async setEquipment(url) {
    if (url.split(':')[0] === 'remove')
      return this.app.webgl.kong.removePartFromName(url.split(':').pop());
    const model = await this.app.core.loader.loadModel(`equip`, url, true);
    this.app.webgl.kong.updatePart(model);
  }

  setArena = async (url) =>
    new Promise((resolve) => {
      this._arenaTl?.kill();
      this._arenaTl = gsap.timeline();

      this._arenaTl.add(this.app.webgl.arena.close());
      this._arenaTl.add(() => {
        this.emit('arenaChangeStart');
      });

      this._arenaTl.add(async () => {
        if (url !== '') {
          if (this.app.webgl.rain) this.app.webgl.rain.visible = false;
          if (this.app.webgl.particles) this.app.webgl.particles.visible = false;

          const model = await this.app.core.loader.loadModel('arena', url, true);
          this.emit('arenaLoaded');

          this.app.webgl.arena.updateModel(model);

          this._arenaTl.add(this.app.webgl.arena.open());
        } else {
          if (this.app.webgl.rain) this.app.webgl.rain.visible = true;
          if (this.app.webgl.particles) this.app.webgl.particles.visible = true;

          // this.background.visible = true;
        }
        this._arenaTl.add(() => {
          this.emit('arenaChangeEnd');
          resolve();
        });
      });
    });

  setAnimation = async (animation, params) => {
    if (this.app.webgl.kong) {
      this.emit('animationStart', animation);
      this.emit('animationChangeStart', animation);

      this.app.webgl.kong.setAnimation(animation)?.then(() => {
        this.emit('animationChangeEnd');
      });
    } else {
      this.app.state.on(EVENTS.ATTACHED, () => {
        this.setAnimation(animation, params);
      });
    }

    // this.emit('animationEnd');
  };

  setAnimationState(value) {
    if (this.app.webgl.kong) {
      if (value === 'paused') {
        this.app.webgl.kong.pauseAnimation();

        this.emit('animationPaused');
      } else if (value === 'running') {
        this.app.webgl.kong.playAnimation();

        this.emit('animationRan');
      }
    }
  }

  setColor = async (color) => this.setColors([color]);

  setColors = async (colors) => {
    const _colors = Array.isArray(colors) ? colors : [colors];

    return new Promise((resolve) => {
      this._colorTl?.kill();
      this._colorTlLoop?.kill();
      this._colorTl = gsap.timeline();

      this._colorTl.add(() => {
        this.emit('colorChangeStart');
      });

      this._goToColor(this._colorTl, _colors[0]);

      this._colorTl.add(() => {
        this.emit('colorChangeEnd');
        resolve();

        if (_colors.length > 1) this._loopBetweenColors(_colors);
      });
    });
  };

  _goToColor = (tl, color) => {
    const dest = new Color(color);
    tl.to(this.app.uniforms.uColor.value, {
      r: dest.r,
      g: dest.g,
      b: dest.b,
      ease: 'power2.inOut',
      duration: 2,
    });
  };

  _loopBetweenColors = (colors) => {
    this._colorTlLoop?.kill();
    this._colorTlLoop = gsap.timeline({ repeat: -1 });

    colors.forEach((color) => {
      this._goToColor(this._colorTlLoop, color);
    });
    this._goToColor(this._colorTlLoop, colors[0]);
  };

  setSize = (width, height) => {
    console.log('Set Size : ', width, height);
    if (this._params.autoResize) return;
    this.app.viewport.setSize(width, height);
  };

  get autoResize() {
    return this.app.tools.viewport.autoResize;
  }

  set autoResize(value) {
    this.app.tools.viewport.autoResize = value;
  }

  setKongPosition = (x, y) => {
    this._positionTl?.kill();
    this._positionTl = gsap.timeline();

    this._positionTl.to(this.app.webgl.scene.position, {
      x,
      z: y,
      ease: 'power2.inOut',
      duration: 1,
    });
  };

  setCameraPosition(pos = [0, 0, 0]) {
    this.app.core.store.get(STORE_KEYS.CAMERA_POSITION).set(...pos);
  }

  setCameraZoom(z = 0) {
    this.app.core.store.set(STORE_KEYS.CAMERA_ZOOM, z);
  }

  setControlsDisabled = (value) => {
    this.app.core.store.set(STORE_KEYS.CONTROLS_DISABLED, !!value);
  };

  setZoomDisabled = (value) => {
    this.app.core.store.set(STORE_KEYS.ZOOM_DISABLED, !!value);
  };

  setPanDisabled = (value) => {
    this.app.core.store.set(STORE_KEYS.PAN_DISABLED, !!value);
  };

  setParallaxDisabled = (value) => {
    this.app.core.store.set(STORE_KEYS.PARALLAX_DISABLED, !!value);
  };

  setKongRotation = (value) => {
    this.app.core.store.set(STORE_KEYS.KONG_ROTATION, (value / 180) * Math.PI);
  };

  setText(config) {
    this._params.text = { ...this._params.text, ...config };
    this.app.webgl.updateText();
  }

  setTextActive(value = true) {
    this._params.text.active = value;
    this.app.webgl.updateText();
  }
  setTextContent(value = '') {
    this._params.text.content = value;
    this.app.webgl.updateText();
  }
  setTextFont(value) {
    this._params.text.font = value;
    this.app.webgl.updateText();
  }
  setTextSize(value) {
    this._params.text.size = value;
    this.app.webgl.updateText();
  }
  setTextPosition(value) {
    this._params.text.position = value;
    this.app.webgl.updateText();
  }
  setTextColor(value) {
    this._params.text.color = value;
    this.app.webgl.updateText();
  }

  setCoolEffect(value) {
    this.ceTl?.kill();
    this.ceTl = gsap.timeline();

    this.ceTl.to(this.app.coolEffectIntensity, {
      value: value ? 1 : 0,
      ease: 'power2.inOut',
      duration: 2,
    });
  }

  setParticlesEffect(value) {
    this.peTl?.kill();
    this.peTl = gsap.timeline();

    this.peTl.to(this.app.particlesEffectIntensity, {
      value: value ? 1 : 0,
      ease: 'power2.inOut',
      duration: 2,
    });
  }

  destroy = () => {
    // TODO on doit vérifier que tout se fait bien comme il faut ici
    this.app.core.ticker.kill();
    this.app.webgl.renderer.domElement?.remove();
    this.app.webgl.controls?.gesture.destroy();
    this.app.webgl.kong?.disposeKong();
    this.app.state.destroy();
  };
}

export { CyberKongzGlViewer };
